import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

const SHOT_PROCESSING_STATUSES = ["finalized", "hdrProcessing", "aiProcessing"];
const SHOT_POLL_INTERVAL_MS = 5000;
// A shot still "processing" after this long is stuck (dead Lambda etc.);
// stop polling for it rather than hammering the API forever.
const SHOT_STALE_AFTER_MS = 15 * 60 * 1000;

const hasInFlightShot = (marketing) =>
  (marketing?.editorShots || []).some(
    (shot) =>
      SHOT_PROCESSING_STATUSES.includes(shot.status) &&
      shot.updatedAt &&
      Date.now() - new Date(shot.updatedAt).getTime() < SHOT_STALE_AFTER_MS,
  );

export const useGetPrepareMarketingByPropertyIdQuery = (propertyId) => {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID, propertyId],
    queryFn: () =>
      api.get(
        `${API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID.replace(
          ":propertyId",
          propertyId
        )}`
      ),
    enabled: !!propertyId,
    // One poll per page (not one per event card), only while a shot is in
    // flight. Paused automatically while the tab is hidden.
    refetchInterval: (query) =>
      hasInFlightShot(query.state.data?.data?.data)
        ? SHOT_POLL_INTERVAL_MS
        : false,
  });
  return {
    data: data?.data?.data ?? null,
    isPending,
    isError,
    error,
    refetch,
  };
};

export const useUpdatePrepareMarketingMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(
          `${API_ROUTES.UPDATE_PREPARE_MARKETING.replace(
            ":propertyId",
            data?.propertyId
          )}`,
          data
        ),
      onSuccess: () => {
        showToast("success", "Prepare Marketing updated successfully");
      },
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries([
          API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID,
          variables?.propertyId,
        ]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// ─── Admin contractor upload mutations ────────────────────────────

/** POST admin notify editor */
export const useAdminNotifyEditorMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ event, images, notes, isExtras, extraImages, shots }) => {
      const extras = Array.isArray(extraImages) ? extraImages : [];
      const res = await api.post("/admin/prepareMarketing/notifyEditor", {
        event,
        images,
        folderPath: "rawPhotos",
        notes,
        isExtras: Boolean(isExtras) || extras.length > 0,
        extraImages: extras,
        shots,
      });

      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminContractorEvents"] });
    },
  });
};

/** PUT admin save images (extras, raw, etc.) */
export const useAdminSaveImagesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, event, images, folderPath }) => {
      const res = await api.put(`/admin/prepareMarketing/${propertyId}`, {
        event,
        images,
        folderPath,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminContractorEvents"] });
    },
  });
};

/** POST admin delete S3 file */
export const useAdminDeleteS3FileMutation = () => {
  return useMutation({
    mutationFn: async ({ key, name, eventId, propertyId, field }) => {
      const res = await api.post("/admin/prepareMarketing/deleteS3File", {
        key,
        name,
        eventId,
        propertyId,
        field,
      });
      return res.data;
    },
  });
};

// ─── Editor upload mutations ──────────────────────────────────────

const presignFileUpload = async ({ propertyId, folderPath, files }) => {
  const res = await api.post("/admin/prepareMarketing/fileUpload", {
    files,
    propertyId,
    folderPath,
  });
  return res.data.presignedUrls;
};

/** Presign + S3 PUT for an editor's edited web/print image. Returns the presigned descriptor (`{ url, key, ... }`). */
export const useUploadEditedImageMutation = () => {
  return useMutation({
    mutationFn: async ({ propertyId, file, folderPath, onUploadProgress }) => {
      const [presigned] = await presignFileUpload({
        propertyId,
        folderPath,
        files: [{ fileName: file.name, fileType: file.type }],
      });
      await api.put(presigned.url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress,
      });
      return presigned;
    },
  });
};

/** Presign + S3 PUT + attach a revision to a rawPhotos image. */
export const useAddRawPhotoRevisionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, eventId, imageId, file, commentId }) => {
      const [presigned] = await presignFileUpload({
        propertyId,
        folderPath: "rawPhotos/revisions",
        files: [{ fileName: file.name, fileType: file.type }],
      });
      await api.put(presigned.url, file, {
        headers: { "Content-Type": file.type },
      });
      const res = await api.post(
        `/admin/prepareMarketing/${propertyId}/rawPhotoRevision`,
        {
          eventId,
          imageId,
          commentId,
          name: file.name,
          size: file.size,
          s3Key: presigned.key,
        }
      );
      return res.data;
    },
    onSuccess: (mergeResult, variables) => {
      if (!mergeResult) return;
      queryClient.invalidateQueries({
        queryKey: [
          API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID,
          variables.propertyId,
        ],
      });
    },
  });
};

/** PUT edited web/print photo lists to send the batch to review. */
export const useSendEditedPhotosToReviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      propertyId,
      editedWebPhotos,
      editedPrintPhotos,
      persistOnly = false,
    }) => {
      const res = await api.put(`/admin/prepareMarketing/${propertyId}`, {
        editedWebPhotos,
        editedPrintPhotos,
        persistOnly,
      });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID,
          variables.propertyId,
        ],
      });
    },
  });
};

/** Generate web (compressed, long-edge 1600) from an uploaded print master. */
export const useGenerateWebFromPrintMutation = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.post(
        "/admin/prepareMarketing/generateWebFromPrint",
        payload,
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to generate web");
      }
      return res.data.data;
    },
  });
};

/** Invoke the authenticated HDR merge wrapper for one editable shot/group. */
export const useHdrMergeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.post("/admin/prepareMarketing/hdrMerge", payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || "HDR merge failed");
      }
      return res.data.data;
    },
    onSuccess: (mergeResult, variables) => {
      if (!mergeResult) return;
      queryClient.invalidateQueries({
        queryKey: [
          API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID,
          variables.propertyId,
        ],
      });
    },
  });
};

const invalidateMarketing = (queryClient, propertyId) => {
  queryClient.invalidateQueries({
    queryKey: [API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID, propertyId],
  });
};

export const useCreateEditorShotMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.put("/admin/editorShots", payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to save shots");
      }
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateMarketing(queryClient, variables.propertyId);
    },
  });
};

export const useFinalizeEditorShotsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.post(
        "/admin/editorShots/finalize",
        payload,
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to finalize shots");
      }
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateMarketing(queryClient, variables.propertyId);
    },
  });
};

export const useUpdateEditorShotMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ shotId, ...payload }) => {
      const res = await api.patch(
        `/admin/editorShots/${shotId}`,
        payload,
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to update shot");
      }
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateMarketing(queryClient, variables.propertyId);
    },
  });
};

export const useUploadEditorShotOverrideMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, eventId, shotId, file }) => {
      const [presigned] = await (async () => {
        const res = await api.post("/admin/prepareMarketing/fileUpload", {
          propertyId,
          folderPath: "hdrOverride",
          files: [{ fileName: file.name, fileType: file.type || "image/jpeg" }],
        });
        return res.data?.presignedUrls || [];
      })();
      if (!presigned?.url) throw new Error("Failed to get upload URL");
      await api.put(presigned.url, file, {
        headers: { "Content-Type": file.type || "image/jpeg" },
      });
      const res = await api.post(
        `/admin/editorShots/${shotId}/override`,
        {
          propertyId,
          eventId,
          key: presigned.key,
          url: presigned.url.split("?")[0],
          name: file.name,
          size: file.size,
        },
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Override upload failed");
      }
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateMarketing(queryClient, variables.propertyId);
    },
  });
};

export const useAiEditEditorShotsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await api.post(
        "/admin/editorShots/aiEdit",
        payload,
      );
      if (!res.data?.success) {
        throw new Error(res.data?.message || "AI edit failed");
      }
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      invalidateMarketing(queryClient, variables.propertyId);
    },
  });
};

/** Classify an image URL via Gemini (admin prepareMarketing). */
export const classifyEditorImage = async (imageUrl, imageId) => {
  const res = await api.post("/admin/editorShots/classify", {
    imageUrl,
    imageId,
  });
  if (res.data?.success && res.data?.data) {
    return {
      imageId,
      category: res.data.data.category,
      description: res.data.data.description,
    };
  }
  return { imageId, category: "INTERNAL", description: null };
};

/**
 * Presign + S3 PUT — contract expected by @beleef/ai-photo-edit EnhanceImageEditor.
 */
export const useFileUpload = (options = {}) =>
  useMutation({
    mutationFn: async ({
      file,
      fileName,
      fileType,
      propertyId,
      folderPath,
      onProgress,
    }) => {
      const fileId = `${fileName}-${Date.now()}`;
      const { data } = await api.post("/admin/prepareMarketing/fileUpload", {
        files: [{ fileId, fileName, fileType }],
        propertyId,
        folderPath,
      });
      const presigned = data.presignedUrls?.[0];
      if (!presigned?.url) throw new Error("No presigned URL returned");

      await api.put(presigned.url, file, {
        headers: { "Content-Type": fileType },
        onUploadProgress: (progressEvent) => {
          if (!onProgress || !progressEvent.total) return;
          onProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total),
          );
        },
      });

      return { url: presigned.url.split("?")[0], key: presigned.key };
    },
    onError: (error) => {
      console.error("FileUpload failed:", error);
      options.onError?.(error);
    },
    onSuccess: (data, variables) => {
      options.onSuccess?.(data, variables);
    },
  });

/**
 * PATCH prepareMarketing AI edit fields — used by EnhanceImageEditor when
 * prepareMarketingId is set.
 */
export const useUpdatePhotoEdit = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ marketingId, data }) => {
      const res = await api.put(
        `/admin/prepareMarketing/aiEdit/${marketingId}`,
        data,
      );
      return res.data;
    },
    onSuccess: async (data, variables) => {
      const updated = data?.data;
      const propertyId = updated?.propertyId;
      if (propertyId) {
        await queryClient.invalidateQueries({
          queryKey: [
            API_ROUTES.GET_PREPARE_MARKETING_BY_PROPERTY_ID,
            propertyId,
          ],
        });
      }
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
};
